import React from 'react';
import { Button } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import ProductCard from '../Cards/ProductCard';
import { IProduct } from '../../Api/Models/Products';
import { useSideScroll } from '../Helpers/useSideScroll';

interface IProductScrollerProps {
    products: IProduct[];
    isDark?: boolean;
    showFeaturedFlag?: boolean;
    // Scroller chrome styling (homePageConfig.scrollButton / scrollbar). scrollButtonStyle applies to
    // both chevron buttons; scrollbarStyle applies to the scrolling row (e.g. scrollbarColor/Width).
    scrollButtonStyle?: React.CSSProperties;
    scrollbarStyle?: React.CSSProperties;
    // Same card presentation overrides as FeaturedProductDisplay — passed straight through to ProductCard.
    cardStyle?: React.CSSProperties;
    cardHoverStyle?: React.CSSProperties;
    nameStyle?: React.CSSProperties;
    priceStyle?: React.CSSProperties;
    imageStyle?: React.CSSProperties;
    addToCartStyle?: React.CSSProperties;
    // When provided (the "Recommended For You" row), each card shows a dismiss (✕) control.
    onDismiss?: (productId: string) => void;
    // Interest-signal source surface, forwarded to each ProductCard.
    source?: string;
}

// Horizontal product scroller — the side-scroll alternative to the wrap grid for a section, modeled on
// the admin app's bundle-search carousel: a nowrap flex row that scrolls horizontally, with chevron
// buttons that appear only when there's more to scroll and page the row by roughly one card width.
const ProductScroller: React.FC<IProductScrollerProps> = (props) => {
    const { ref: scrollRef, canScrollLeft, canScrollRight, checkScroll, scrollByItem } =
        useSideScroll('.card', 16, [props.products]);

    const arrowBg = props.isDark ? 'rgba(40,42,54,0.85)' : 'rgba(255,255,255,0.9)';

    return (
        <div className='card-scroller-wrap'>
            {canScrollLeft &&
                <Button
                    className='card-scroller-arrow card-scroller-arrow-left'
                    shape='circular'
                    appearance='subtle'
                    icon={<ChevronLeftRegular />}
                    aria-label='Scroll left'
                    style={{ background: arrowBg, ...props.scrollButtonStyle }}
                    onClick={() => scrollByItem('left')}
                />
            }
            <div className='card-scroller' ref={scrollRef} onScroll={checkScroll} style={props.scrollbarStyle}>
                {props.products.map((product) => (
                    <ProductCard
                        showAddToCart={true}
                        key={product.id + product.name}
                        product={product}
                        isDark={props.isDark}
                        showFeaturedFlag={props.showFeaturedFlag}
                        cardStyle={props.cardStyle}
                        cardHoverStyle={props.cardHoverStyle}
                        nameStyle={props.nameStyle}
                        priceStyle={props.priceStyle}
                        imageStyle={props.imageStyle}
                        addToCartStyle={props.addToCartStyle}
                        onDismiss={props.onDismiss}
                        source={props.source}
                    />
                ))}
            </div>
            {canScrollRight &&
                <Button
                    className='card-scroller-arrow card-scroller-arrow-right'
                    shape='circular'
                    appearance='subtle'
                    icon={<ChevronRightRegular />}
                    aria-label='Scroll right'
                    style={{ background: arrowBg, ...props.scrollButtonStyle }}
                    onClick={() => scrollByItem('right')}
                />
            }
        </div>
    );
};

export default ProductScroller;
